import { CustomerStatus } from '@app/utils/consts';
import { ListInterface, ListResponse, TagInterface } from './util';
import { PlanInterface } from './plans';
import { SubscriptionInterface } from './subscription';
import { UserInterface } from './user';

export type { PlanInterface };

export interface BusinessPhotoInterface {
  id: string;
  businessId: string;
  url: string;
  createdAt: string;
  updatedAt: string;
}

export interface BusinessNoteInterface {
  id: string;
  businessId: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

export interface BusinessCategoryInterface {
  id: string;
  name: string;
}

export interface PlaceInterface {
  name: string;
  address: string;
}

export interface PlacesSearchResponse {
  message: string;
  places: PlaceInterface[];
  count: number;
}

export interface BusinessInterface {
  id: string;
  name: string;
  email: string;
  phoneNumber?: string | null;
  websiteUrl: string | null;
  categoryId: string;
  subcategory: string | null;
  address: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  photo?: BusinessPhotoInterface | null;
}

export interface CustomerInterface {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  notes?: string;
  userId: string;
  id: string;
  createdAt: string;
  updatedAt: string;
  lists: ListInterface[];
  tags: TagInterface[];
  status?: CustomerStatus;
  source?: string;
  sourceType?: string;
  rating?: number;
  unsubscribed?: boolean;
}

// Business Member Permissions
export interface BusinessMemberPermissions {
  // REVIEWS
  respondToReviews?: boolean;

  // CUSTOMERS
  addCustomers?: boolean;
  editCustomers?: boolean;
  deleteCustomers?: boolean;
  editLists?: boolean;

  // CAMPAIGNS & AUTOMATIONS
  createCampaigns?: boolean;
  cancelCampaigns?: boolean;
  createAutomations?: boolean;
  cancelAutomations?: boolean;

  // GLOBAL
  editBusinessInfo?: boolean;
  editTeamMembers?: boolean;
  editPaymentMethods?: boolean;
  changePackage?: boolean;
}

export type PermissionKey = keyof BusinessMemberPermissions;

export const PERMISSION_LABELS: Record<PermissionKey, string> = {
  respondToReviews: 'Respond to reviews',
  addCustomers: 'Add new customers',
  editCustomers: 'Edit customer information',
  deleteCustomers: 'Delete customers',
  editLists: 'Add and edit lists',
  createCampaigns: 'Create new campaigns',
  cancelCampaigns: 'Cancel campaigns',
  createAutomations: 'Create new automations',
  cancelAutomations: 'Cancel automations',
  editBusinessInfo: 'View and edit business information',
  editTeamMembers: 'View, edit, and add team members',
  editPaymentMethods: 'View and edit payment methods',
  changePackage: 'Change the software package',
};

export const PERMISSION_CATEGORIES = {
  REVIEWS: {
    label: 'Reviews',
    permissions: ['respondToReviews'] as PermissionKey[],
  },
  CUSTOMERS: {
    label: 'Customers',
    permissions: [
      'addCustomers',
      'editCustomers',
      'deleteCustomers',
      'editLists',
    ] as PermissionKey[],
  },
  CAMPAIGNS_AUTOMATIONS: {
    label: 'Campaigns & Automations',
    permissions: [
      'createCampaigns',
      'cancelCampaigns',
      'createAutomations',
      'cancelAutomations',
    ] as PermissionKey[],
  },
  GLOBAL: {
    label: 'Global',
    permissions: [
      'editBusinessInfo',
      'editTeamMembers',
      'editPaymentMethods',
      'changePackage',
    ] as PermissionKey[],
  },
};

export type BusinessMemberUserInterface = Pick<
  UserInterface,
  'id' | 'firstName' | 'lastName' | 'email' | 'lastActiveAt' | 'status'
> & {
  photo?: string;
};

export interface BusinessMemberInterface {
  id: string;
  userId: string | null;
  businessId: string;
  isOwner: boolean;
  permissions: BusinessMemberPermissions | null;
  jobTitle?: string | null;
  email?: string | null;
  status?: 'active' | 'pending';
  lastActivity?: string;
  createdAt: string;
  updatedAt: string;
  user: BusinessMemberUserInterface | null;
  invite?: {
    id: string;
    token: string;
    status: string;
    expiresAt: string;
  } | null;
}

export type BusinessSubscriptionInterface = Omit<
  SubscriptionInterface,
  'stripeCustomerId' | 'stripeSubscriptionId' | 'plan' | 'trialEnd'
> & {
  planId: number | null;
  stripeSubscriptionId: string | null;
  trialEnd: number | null;
  plan?: PlanInterface | null;
};

export interface AdminBusinessInterface extends BusinessInterface {
  category?: BusinessCategoryInterface;
  subscription?: BusinessSubscriptionInterface | null;
  members?: BusinessMemberInterface[];
  lastActiveAt: string | undefined;
  googleIntegrationDate: string | undefined;
  lastReviewRequestDate: string | undefined;
  lastReviewRequestCount: number | undefined;
  note?: BusinessNoteInterface | null;
}

export interface PaginatedBusinessesResponse
  extends ListResponse<AdminBusinessInterface> {
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}
